import config from '../../utils/config';

const ADMIN_ROUTE = '/wms/admin-settings';
const ADMIN_PERMISSION = 'admin-settings';
const ORIGIN = config.origin;

let cachedMenu = null;

// Used to gate the route and show the Admin tab since Admin is not in the menu tree
export function isAdminSettingsPath(pathname) {
    if (!pathname) return false;
    return pathname === ADMIN_ROUTE || pathname.startsWith(`${ADMIN_ROUTE}/`);
}

export function getWmsMenuTree() {
    return cachedMenu || [];
}

export function clearWmsMenuCache() {
    cachedMenu = null;
}

export async function loadWmsMenuTree() {
    if (cachedMenu) return cachedMenu;

    const response = await fetch(`${ORIGIN}/wms/menu`);
    if (!response.ok) throw new Error('Failed to load WMS menu');

    const result = await response.json();
    if (!Array.isArray(result?.menu)) throw new Error('Invalid WMS menu response');

    cachedMenu = result.menu;
    return cachedMenu;
}

export function pathMatchesTaskRoute(pathname, taskRoute) {
    if (!pathname || !taskRoute) return false;
    if (taskRoute === '/wms') {
        return pathname === '/wms' || pathname === '/wms/';
    }
    return pathname === taskRoute || pathname.startsWith(`${taskRoute}/`);
}

export function filterMenuByPermissions(menu, permissions = []) {
    const permissionSet = new Set(permissions);

    return (menu || [])
        .map((category) => ({
            ...category,
            tasks: (category.tasks || []).filter((task) => permissionSet.has(task.permissionKey)),
        }))
        .filter((category) => category.tasks.length > 0);
}

export function flattenPermittedTasks(menu, permissions = []) {
    return filterMenuByPermissions(menu, permissions).flatMap((category) => category.tasks || []);
}

export function findCategoryIdForPath(menu, pathname) {
    for (const category of menu || []) {
        if ((category.tasks || []).some((task) => pathMatchesTaskRoute(pathname, task.route))) {
            return category.id;
        }
    }
    return null;
}

export function findCategoryById(menu, categoryId) {
    return (menu || []).find((category) => category.id === categoryId) || null;
}

export function findTaskForPath(menu, pathname) {
    for (const category of menu || []) {
        for (const task of category.tasks || []) {
            if (pathMatchesTaskRoute(pathname, task.route)) return task;
        }
    }
    return null;
}

// Resolve required permission for a WMS pathname (menu tasks + admin)
export function getRequiredPermissionForPath(pathname) {
    if (!pathname) return null;
    if (isAdminSettingsPath(pathname)) {
        return ADMIN_PERMISSION;
    }

    // Legacy Trace2 URL alias — same Guided Interface, still gated by Trace2 permission
    if (pathname === '/wms/trace2' || pathname.startsWith('/wms/trace2/')) {
        return 'Trace2';
    }

    const task = findTaskForPath(getWmsMenuTree(), pathname);
    return task?.permissionKey || null;
}

export function userHasPermission(permissions = [], permissionKey) {
    if (!permissionKey) return false;
    const normalizedNeeded = permissionKey.toLowerCase();
    return permissions.some((permission) => String(permission).toLowerCase() === normalizedNeeded);
}

export function resolveDefaultTaskRoute(permissions = [], defaultTaskKey = '') {
    const permittedTasks = flattenPermittedTasks(getWmsMenuTree(), permissions);

    if (defaultTaskKey) {
        const defaultTask = permittedTasks.find((task) => task.permissionKey === defaultTaskKey);
        if (defaultTask?.route) return defaultTask.route;
    }

    if (permittedTasks[0]?.route) return permittedTasks[0].route;

    if (userHasPermission(permissions, ADMIN_PERMISSION)) {
        return ADMIN_ROUTE;
    }

    return '/wms';
}
